import { Injectable } from '@angular/core';
import { IChartTitleData } from 'app/modules/shared/interfaces/card-data';
import { from, Observable } from 'rxjs';
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import {
    CompensationSmallCardData,
    CompensationByTypeLargeCardData,
    FundedvsCompensationLargeCardData,
    DrawSmallCardsData,
    DrawStatusLargeCardData,
    DistributionOfCompensationPaidLargeCardData,
    GLAAGSmallCardData,
    GLAAGLargeCardData,
    Incentives,
    ILabelCollection,
    GLAAGIncomeStatementForm,
    GLAAGVolumeFundedForm,
    IGLData,
} from '../interfaces/incentives-data';
import { ICamparisonInfo, ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { LimeGearCharts } from 'app/modules/shared/classes/chart-data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import * as Highcharts from 'highcharts';
import { IncentivesCharts } from 'app/modules/shared/classes/incentives-chart-options';
import { MatDialog } from '@angular/material/dialog';
import { FinancialsAagDialogComponent } from 'app/modules/shared/components/financials-aag-dialog/financials-aag-dialog.component';

@Injectable({
    providedIn: 'root',
})
export class IncentivesService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'incentives';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private http: HttpClient,
        private reusableFunctions: ReusableFunctions,
        private matDialog: MatDialog
    ) {}

    getCompensationSmallCardData(
        fromDatePeriod: string,
        tenured: number,
        active: number,
        LOonly: boolean,
        hFilterJSON: string
    ): Observable<CompensationSmallCardData[]> {
        return this.http.post<CompensationSmallCardData[]>(this._url + '/compensationSmallCard', {
            FromDatePeriod: fromDatePeriod,
            Tenured: tenured,
            EmpStatus: active,
            LOOnly: LOonly,
            HFilterJSON: hFilterJSON,
        });
    }

    getDrawSmallCardsData(
        fromDatePeriod: string,
        tenured: number,
        active: number,
        LOonly: boolean,
        hFilterJSON: string
    ): Observable<DrawSmallCardsData[]> {
        return this.http.post<DrawSmallCardsData[]>(this._url + '/drawSmallCards', {
            FromDatePeriod: fromDatePeriod,
            Tenured: tenured,
            EmpStatus: active,
            LOOnly: LOonly,
            HFilterJSON: hFilterJSON,
        });
    }

    getCompensationByTypeLargeCard(
        fromDatePeriod: string,
        tenured: number,
        active: number,
        hFilterJSON: string
    ): Observable<CompensationByTypeLargeCardData[]> {
        return this.http.post<CompensationByTypeLargeCardData[]>(
            this._url + '/compensationByTypeLargeCard',
            {
                FromDatePeriod: fromDatePeriod,
                Tenured: tenured,
                EmpStatus: active,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    getFundedvsCompensationLargeCardData(
        fromDatePeriod: string,
        tenured: number,
        active: number,
        hFilterJSON: string
    ): Observable<FundedvsCompensationLargeCardData[]> {
        return this.http.post<FundedvsCompensationLargeCardData[]>(
            this._url + '/fundedvsCompensationLargeCard',
            {
                FromDatePeriod: fromDatePeriod,
                Tenured: tenured,
                EmpStatus: active,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    getDistributionOfCompensationPaidLargeCardData(
        fromDatePeriod: string,
        tenured: number,
        active: number,
        hFilterJSON: string
    ): Observable<DistributionOfCompensationPaidLargeCardData[]> {
        return this.http.post<DistributionOfCompensationPaidLargeCardData[]>(
            this._url + '/distributionOfCompensationPaidLargeCard',
            {
                FromDatePeriod: fromDatePeriod,
                Tenured: tenured,
                EmpStatus: active,
                HFilterJSON: hFilterJSON,
            }
        );
    }

    getDrawStatusLargeCardData(
        fromDatePeriod: string,
        tenured: number,
        active: number,
        hFilterJSON: string
    ): Observable<DrawStatusLargeCardData[]> {
        return this.http.post<DrawStatusLargeCardData[]>(this._url + '/drawStatusLargeCard', {
            FromDatePeriod: fromDatePeriod,
            Tenured: tenured,
            EmpStatus: active,
            HFilterJSON: hFilterJSON,
        });
    }

    getGLAAGSmallCardData(parameters, loanAccount: number): Observable<GLAAGSmallCardData[]> {
        parameters.loanAccount = loanAccount;
        return this.http.post<GLAAGSmallCardData[]>(
            this._url + '/displayGLAAGSmallCard',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getGLAAGLargeCardData(parameters): Observable<GLAAGLargeCardData[]> {
        return this.http.post<GLAAGLargeCardData[]>(
            this._url + '/displayGLAAGLargeCard',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getGLAAIncomeVolumeData(
        parameters: GLAAGIncomeStatementForm | GLAAGVolumeFundedForm,
        isVolumeFunded: boolean
    ): Observable<IGLData[]> {
        if (isVolumeFunded) {
            return this.http.post<IGLData[]>(
                this._url + '/displayGLAAGFundedVolumeByMonth',
                parameters,
                {
                    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                }
            );
        } else {
            return this.http.post<IGLData[]>(
                this._url + '/displayGLAAGIncomeStatement',
                parameters,
                {
                    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                }
            );
        }
    }

    setAllChartSeriesOptions(
        cardInfo: ICardInfo,
        lineData: IncentivesCharts.ChartDataPoint[],
        stackColumnData: IncentivesCharts.ChartdataSeries[],
        compareTo: string,
        cardDrilldownUrl: string,
        compareToFriendly: string,
        loanPurpose: string,
        loanType: string,
        limeGearCharts: LimeGearCharts.LimeGearChart
    ) {
        let seresData: IncentivesCharts.ChartdataSeries[] = [];
        if (lineData) {
            let lineSeries = limeGearCharts.setLineChartSeriesOptions(
                lineData,
                'spline',
                '#A6266E',
                'circle',
                false,
                compareTo,
                cardDrilldownUrl,
                this.reusableFunctions.formatTitle(cardInfo.name),
                compareToFriendly,
                loanPurpose,
                loanType
            );
            seresData = stackColumnData.concat([lineSeries as IncentivesCharts.ChartdataSeries]);
        }
        return seresData;
    }

    setChartData(
        stackData: any[],
        lineData: any[],
        property: string,
        alternateProperty: string,
        groupByClass: string,
        tootipTotalName: string,
        colors: object,
        dateRange: string
    ) {
        let dataSet = stackData.map((obj) => ({ ...obj }));
        let stackColumnsData: IncentivesCharts.ChartdataSeries[] = [];
        let xAxisLabelList: ILabelCollection[] = [];
        let groupingNames = this.reusableFunctions.getUniqueItemList(dataSet, groupByClass);
        if (groupingNames[0] === 'Expense') {
            groupingNames.reverse();
        }
        groupingNames.forEach((ItemClass) => {
            let classData = dataSet.filter((item) => item[groupByClass] === ItemClass);
            let dataInf = this.setPointData(
                classData,
                property,
                alternateProperty,
                tootipTotalName,
                dateRange
            );
            const itemClassName = this.addNonLoanVerbiage(ItemClass);
            stackColumnsData.push({
                name: itemClassName,
                data: dataInf.dataSet,
                color: colors[ItemClass],
                type: 'column',
            });
            dataInf?.xLabelCollection?.forEach((xLabelInf) => {
                let indx = xAxisLabelList.findIndex(
                    (labelInf) => labelInf.index === xLabelInf.index
                );
                if (indx < 0) {
                    xAxisLabelList.push({
                        index: xLabelInf.index,
                        description: xLabelInf.description,
                    });
                }
            });
        });
        let lineChartData = this.setLineChartData(
            lineData,
            property,
            alternateProperty,
            xAxisLabelList,
            dateRange
        );
        return { stackColumnsData: stackColumnsData, lineData: lineChartData };
    }

    setPointData(
        classData: Incentives[],
        property: string,
        alternateProperty: string,
        tootipTotalName: string,
        dateRange: string
    ) {
        let purposeItems: IncentivesCharts.ChartDataPoint[] = [];
        let xLabelCollection: ILabelCollection[] = [];
        classData.forEach((item) => {
            let name = this.reusableFunctions.setXValues(item, dateRange);
            xLabelCollection.push({
                index: item.period,
                description: item.yearMonthCalendar,
            });
            purposeItems.push({
                y: item[property],
                name: name,
                properties: {
                    datePeriodDescription: item.yearMonthCalendar,
                    period: item.period,
                    units: property,
                    tootipTotalName: tootipTotalName,
                    alternateY: item[alternateProperty],
                },
            });
        });
        return { dataSet: purposeItems, xLabelCollection: xLabelCollection };
    }

    setLineChartData(
        data: Incentives[],
        property: string,
        alternateProperty: string,
        xlabelsList: ILabelCollection[],
        dateRange: string
    ) {
        data.forEach((item, i) => (item.period = i));

        let seriesData: IncentivesCharts.ChartDataPoint[] = [];
        if (data.length > 0) {
            let dataSet = data.map((obj) => ({ ...obj }));
            let groupingNames = this.reusableFunctions.getUniqueItemList(dataSet, 'period');
            groupingNames.forEach((period, index) => {
                let groupsData = dataSet.filter((item) => item.period === period);

                if (groupsData.length > 0 && xlabelsList.length > index) {
                    let xLabel: number | string = groupsData[0].period;
                    if (this.reusableFunctions.getChartXLabel(dateRange) === 'Month') {
                        xLabel = xlabelsList[index]?.description?.slice(0, 3);
                    }
                    seriesData.push({
                        y: this.reusableFunctions.calculateSumByProperty(groupsData, property),
                        name: xLabel.toString(),
                        properties: {
                            yearMonthCalendar: groupsData[0].yearMonthCalendar,
                            period: groupsData[0].period,
                            units: this.reusableFunctions.calculateSumByProperty(groupsData, 'nbr'),
                            alternateY: groupsData[0][alternateProperty],
                            type: property,
                        },
                    });
                }
            });
        }
        return seriesData;
    }

    updateChartData(
        cardInfo: ICardInfo,
        chartOptions: Highcharts.Options,
        hasData: boolean,
        isDataReady: boolean,
        action: NoDataActionType,
        comparison: ICamparisonInfo,
        mainValue: number,
        secondaryValue: number,
        showUnits,
        isComparisonInverse: boolean,
        loanPurpose: string,
        loanType: string,
        compareTo: string
    ) {
        let largeCardInteriorOptions = Object.assign({}, cardInfo.largeCardInteriorOptions);
        if (hasData) {
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        let queryParams = {
            purpose: loanPurpose,
            loanType: loanType,
            compareTo: compareTo,
            periodGroup: 'T',
        };

        if (cardInfo.urlSegment.search('fall-out') > -1) {
            queryParams['falloutType'] = 'All';
        }

        let chartTitleData;

        if (comparison) {
            chartTitleData = this.getChartTitleData(
                cardInfo.name,
                comparison.comparisonValue,
                comparison.percentage,
                comparison.isComparable,
                null,
                mainValue,
                secondaryValue,
                false,
                showUnits,
                isComparisonInverse
            );
        }

        largeCardInteriorOptions.chartTitleData = chartTitleData;
        largeCardInteriorOptions.queryParams = Object.assign({}, queryParams);
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = isDataReady;
        largeCardInteriorOptions.searchTerm = '';
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.showCardControlArea = true;
        return Object.assign({}, largeCardInteriorOptions);
    }

    getChartTitleData(
        title: string,
        compareValue: number,
        comparisonPercent: number,
        isComparable: boolean,
        subValue: string,
        mainValue: number,
        secondaryValue: number,
        isSubValueVisible: boolean,
        ismainValueUnit: boolean,
        isInverseCompareValue: boolean
    ) {
        let isMainValueUnitVisible = true;
        let mainValueToShow: string = '';
        let secondaryValueToShow: string = '';
        let comparison = this.reusableFunctions.getCaret(
            compareValue,
            isInverseCompareValue,
            comparisonPercent,
            isComparable
        );
        let comparisonValue = comparison.comparison;

        if (ismainValueUnit) {
            mainValueToShow = this.reusableFunctions.formatUnits(mainValue);
            comparisonValue = comparisonValue.replace('$', '');
            secondaryValueToShow = this.reusableFunctions.formatMoney(secondaryValue);
        } else {
            mainValueToShow = this.reusableFunctions.formatMoney(mainValue);
            comparisonValue;
            secondaryValueToShow = this.reusableFunctions.formatUnits(secondaryValue);
        }

        if (subValue === null) {
            isSubValueVisible = false;
        }

        let chartTitleData: IChartTitleData;
        if (ismainValueUnit) {
            chartTitleData = {
                showChartCustomTitle: true,
                chartTile: title,
                chartLabelColor: comparison.color,
                chartComparisonValue: comparisonValue,
                chartMainValue: secondaryValueToShow,
                chartSecondaryValue: mainValueToShow,
                isMainValueUnitVisible: isMainValueUnitVisible,
                chartSubItemValue: subValue,
                isChartSubValueVisible: isSubValueVisible,
                svgIconString: comparison.svgData,
            };
        } else {
            chartTitleData = {
                showChartCustomTitle: true,
                chartTile: title,
                chartLabelColor: comparison.color,
                chartComparisonValue: comparisonValue,
                chartMainValue: mainValueToShow,
                chartSecondaryValue: secondaryValueToShow,
                isMainValueUnitVisible: isMainValueUnitVisible,
                chartSubItemValue: subValue,
                isChartSubValueVisible: isSubValueVisible,
                svgIconString: comparison.svgData,
            };
        }
        return chartTitleData;
    }

    addNonLoanVerbiage(item: string) {
        if (item != 'Prior Period' && !item.includes('Loan')) {
            if (item.includes('Expense')) {
                return 'Non-Loan ' + item + 's';
            }
            return 'Non-Loan ' + item;
        }
        return item;
    }

    openAAGModal(
        ymc: string,
        title: string,
        value: number,
        bps: number,
        isPriorPeriod: boolean,
        hFilterJSON: string
    ) {
        let loanAccount = title.includes('Non') ? 0 : 1;
        if (title.includes('Total')) loanAccount = 2;
        const parameters = {
            fromDatePeriod: null,
            ymc,
            loanAccount,
            hFilterJSON,
        };
        const headerInfo = { title, value, bps, isSingleMonth: true, isPriorPeriod };
        this.getGLAAIncomeVolumeData(parameters, false).subscribe(
            (data) => {
                const dialogRef = this.matDialog.open(FinancialsAagDialogComponent, {
                    data: { headerData: headerInfo, dialogData: data },
                    panelClass: 'financials-aag-dialog',
                });
            },
            (error) => {
                console.log('error: ', error);
            }
        );
    }
}
